import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  iDataNivel,
  iDataNivelAsignatura,
} from "../../../interfaces/SuperAdmin/Pages/iDataNivel";
import { iDataAsignatura } from "../../../interfaces/SuperAdmin/Pages/iDataAsignatura";
import { toast } from "react-toastify";
import {
  assignAsignToNivel,
  getAsignaturasByNivelId,
  getNivelById,
  unassignAsignToNivel,
} from "../../../api/broker/nivel";
import { getAsignaturasByOrgId } from "../../../api/broker/asignatura";

interface SingleNivelContextProps {
  nivel: iDataNivel | null;
  nivelAsigns: iDataNivelAsignatura[];
  allAsignaturas: iDataAsignatura[];
  selectedNivelAsign: string[];

  isLoadingInfo: boolean;
  isLoadingAllAsigns: boolean;
  isAdding: boolean;
  isDeleting: boolean;

  isDeleteModalOpen: boolean;
  isAddModalOpen: boolean;

  searchText: string;

  toggleAddModal: (isOpen: boolean) => void;
  toggleDeleteModal: (isOpen: boolean) => void;
  deleteSelections: (body: { ids: string[] }) => Promise<void>;
  submitSelections: (body: { ids: string[] }) => Promise<void>;
  setSearchText: Dispatch<SetStateAction<string>>;
  setSelectedNivelAsign: Dispatch<SetStateAction<string[]>>;
}

const SingleNivelContext = createContext<SingleNivelContextProps>(
  {} as SingleNivelContextProps
);

interface Props {
  children: React.ReactNode;
}

const SingleNivelProvider: FC<Props> = ({ children }) => {
  const { idOrganization, idNivel } = useParams();

  const [nivel, setNivel] = useState<iDataNivel | null>(null);
  const [nivelAsigns, setNivelAsigns] = useState<iDataNivelAsignatura[]>([]);
  const [selectedNivelAsign, setSelectedNivelAsign] = useState<string[]>([]);
  const [allAsignaturas, setAllAsignaturas] = useState<iDataAsignatura[]>([]);

  const [isLoadingInfo, setIsLoadingInfo] = useState(true);
  const [isLoadingAllAsigns, setIsLoadingAllAsigns] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [searchText, setSearchText] = useState("");

  // Fetch del nivel y de sus asignaturas
  const fetchInfo = async (id: string) => {
    setIsLoadingInfo(true);
    try {
      const [nivelRes, asignRes] = await Promise.all([
        getNivelById(id),
        getAsignaturasByNivelId(id),
      ]);

      console.log(nivelRes);
      if (nivelRes.data.statusCode == 200) {
        setNivel(nivelRes.data.body);
      } else {
        toast.error("Error al obtener el nivel");
      }

      console.log(asignRes);
      if (asignRes.data.statusCode == 200) {
        setNivelAsigns(asignRes.data.body);
      } else {
        toast.error("Error al obtener las asignaturas del nivel");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las asignaturas del nivel o el nivel");
    } finally {
      setIsLoadingInfo(false);
    }
  };

  // Función para traer todas las asignaturas de la organización
  const fetchAllAsigns = async () => {
    if (idOrganization) {
      setIsLoadingAllAsigns(true);
      try {
        const res = await getAsignaturasByOrgId(idOrganization);
        console.log(res);

        if (res.data.statusCode === 200) {
          setAllAsignaturas(res.data.body);
        } else {
          toast.error("Error al obtener las asignaturas de la organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener las asignaturas de la organización");
      } finally {
        setIsLoadingAllAsigns(false);
      }
    }
  };

  // Apenas se renderize el componente, hacemos el fetch
  useEffect(() => {
    if (idNivel) {
      fetchInfo(idNivel);
    }
  }, [idNivel]);

  // Abrir y cerrar modal de agregar asignaturas a un nivel
  const toggleAddModal = (isOpen: boolean) => {
    if (isOpen && !allAsignaturas.length) {
      fetchAllAsigns();
    }
    setIsAddModalOpen(isOpen);
  };

  // Abrir y cerrar modal de eliminar asignaturas de un nivel
  const toggleDeleteModal = (isOpen: boolean) => {
    setIsDeleteModalOpen(isOpen);
  };

  // Quitar asignaturas del nivel
  const deleteSelections = async (body: { ids: string[] }) => {
    setIsDeleting(true);
    try {
      const res = await unassignAsignToNivel(body);
      console.log(res);

      if (res.data.statusCode === 200) {
        setSelectedNivelAsign([]);
        setNivelAsigns((prev) =>
          prev.filter((item) => !body.ids.includes(item.id_nivel_asignatura))
        );
        toggleDeleteModal(false);
        toast.success("Asignaturas eliminadas del nivel exitosamente");
      } else {
        toast.error("Error al eliminar las asignaturas del nivel");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar las asignaturas del nivel");
    } finally {
      setIsDeleting(false);
    }
  };

  // Agregar asignaturas a nivel
  const submitSelections = async (body: { ids: string[] }) => {
    if (idNivel) {
      setIsAdding(true);
      try {
        const res = await assignAsignToNivel(body, idNivel);
        console.log(res);

        if (res.data.statusCode === 201) {
          setNivelAsigns((prev) => {
            return [...prev, ...res.data.body];
          });
          setIsAddModalOpen(false);
          toast.success("Asignaturas agregadas al nivel exitosamente");
        } else {
          toast.error("Error al agregar las asignaturas al nivel");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al agregar las asignaturas al nivel");
      } finally {
        setIsAdding(false);
      }
    }
  };

  return (
    <SingleNivelContext.Provider
      value={{
        isLoadingInfo,
        isLoadingAllAsigns,
        isDeleting,
        isAdding,
        nivel,
        allAsignaturas,
        selectedNivelAsign,
        nivelAsigns,
        isDeleteModalOpen,
        isAddModalOpen,
        searchText,
        deleteSelections,
        submitSelections,
        toggleAddModal,
        toggleDeleteModal,
        setSearchText,
        setSelectedNivelAsign,
      }}
    >
      {children}
    </SingleNivelContext.Provider>
  );
};

const useSingleNivel = () => useContext(SingleNivelContext);

export { SingleNivelProvider, useSingleNivel };
